// =====================================================
// SERVIÇO DE INTEGRAÇÃO COM STRIPE
// =====================================================

import { 
  StripeCustomer, 
  StripeProduct, 
  StripeSubscriptionRequest, 
  StripeSubscriptionResponse,
  Plan,
  Subscription 
} from '../types/user';

class StripeService {
  private apiKey: string;
  private publishableKey: string;
  private baseUrl: string;

  constructor() {
    this.apiKey = import.meta.env.VITE_STRIPE_SECRET_KEY || '';
    this.publishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '';
    this.baseUrl = 'https://api.stripe.com/v1';
    
    if (!this.apiKey || !this.publishableKey) {
      console.warn('⚠️ Stripe keys não configuradas. Usando modo simulado.');
    }
  }

  // =====================================================
  // MÉTODOS AUXILIARES
  // =====================================================

  private async makeRequest(endpoint: string, options: RequestInit = {}) {
    const url = `${this.baseUrl}${endpoint}`;
    
    if (!this.apiKey) {
      console.log('🎭 [MODO SIMULADO] Stripe request:', { url, options });
      return this.simulateResponse(endpoint, options);
    }

    const headers = {
      'Authorization': `Bearer ${this.apiKey}`,
      'Content-Type': 'application/x-www-form-urlencoded',
      ...options.headers,
    };

    try {
      const response = await fetch(url, {
        ...options,
        headers,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`Stripe API Error: ${response.status} - ${JSON.stringify(errorData)}`);
      }

      return await response.json();
    } catch (error) {
      console.error('❌ Erro na requisição Stripe:', error);
      throw error;
    }
  }

  private simulateResponse(endpoint: string, options: RequestInit) {
    console.log('🎭 Simulando resposta Stripe para:', endpoint);
    
    // Simular diferentes tipos de resposta baseado no endpoint
    if (endpoint.includes('/customers') && options.method === 'POST') {
      return {
        id: 'cus_' + Math.random().toString(36).substr(2, 14),
        object: 'customer',
        email: 'cliente@exemplo.com',
        name: 'Cliente Simulado',
        created: Math.floor(Date.now() / 1000),
        default_source: null,
        description: null,
        metadata: {},
      };
    }

    if (endpoint.includes('/products') && options.method === 'POST') {
      return {
        id: 'prod_' + Math.random().toString(36).substr(2, 14),
        object: 'product',
        name: 'Produto Simulado',
        active: true,
        created: Math.floor(Date.now() / 1000),
        metadata: {},
      };
    }

    if (endpoint.includes('/prices') && options.method === 'POST') {
      return {
        id: 'price_' + Math.random().toString(36).substr(2, 14),
        object: 'price',
        active: true,
        currency: 'brl',
        product: 'prod_simulado',
        unit_amount: 2990,
        recurring: {
          interval: 'month',
          interval_count: 1
        },
        created: Math.floor(Date.now() / 1000),
      };
    }

    if (endpoint.includes('/checkout/sessions') && options.method === 'POST') {
      return {
        id: 'cs_' + Math.random().toString(36).substr(2, 20),
        object: 'checkout.session',
        url: 'https://checkout.stripe.com/pay/cs_simulado#simulado',
        payment_status: 'unpaid',
        status: 'open',
        created: Math.floor(Date.now() / 1000),
      };
    }

    if (endpoint.includes('/subscriptions') && options.method === 'POST') {
      return {
        id: 'sub_' + Math.random().toString(36).substr(2, 14),
        object: 'subscription',
        status: 'active',
        customer: 'cus_simulado',
        created: Math.floor(Date.now() / 1000),
        current_period_start: Math.floor(Date.now() / 1000),
        current_period_end: Math.floor((Date.now() + 30 * 24 * 60 * 60 * 1000) / 1000),
        items: {
          data: [
            {
              id: 'si_simulado',
              price: {
                id: 'price_simulado',
                unit_amount: 2990,
                currency: 'brl'
              }
            }
          ]
        }
      };
    }

    return { success: true, simulated: true };
  }

  // =====================================================
  // GESTÃO DE CLIENTES
  // =====================================================

  async createCustomer(customerData: StripeCustomer) {
    console.log('📧 Criando cliente Stripe:', customerData.email);
    
    const formData = new URLSearchParams();
    formData.append('email', customerData.email);
    formData.append('name', customerData.name);
    if (customerData.phone) formData.append('phone', customerData.phone);
    if (customerData.address) {
      formData.append('address[line1]', customerData.address.line1);
      formData.append('address[city]', customerData.address.city);
      formData.append('address[state]', customerData.address.state);
      formData.append('address[postal_code]', customerData.address.postal_code);
      formData.append('address[country]', customerData.address.country);
    }

    try {
      const response = await this.makeRequest('/customers', {
        method: 'POST',
        body: formData,
      });

      console.log('✅ Cliente Stripe criado:', response.id);
      return response;
    } catch (error) {
      console.error('❌ Erro ao criar cliente Stripe:', error);
      throw error;
    }
  }

  async updateCustomer(customerId: string, customerData: Partial<StripeCustomer>) {
    console.log('📝 Atualizando cliente Stripe:', customerId);
    
    const formData = new URLSearchParams();
    if (customerData.email) formData.append('email', customerData.email);
    if (customerData.name) formData.append('name', customerData.name);
    if (customerData.phone) formData.append('phone', customerData.phone);

    try {
      const response = await this.makeRequest(`/customers/${customerId}`, {
        method: 'POST',
        body: formData,
      });

      console.log('✅ Cliente Stripe atualizado:', customerId);
      return response;
    } catch (error) {
      console.error('❌ Erro ao atualizar cliente Stripe:', error);
      throw error;
    }
  }

  // =====================================================
  // GESTÃO DE PRODUTOS E PREÇOS
  // =====================================================

  async createProduct(productData: StripeProduct) {
    console.log('📋 Criando produto Stripe:', productData.name);
    
    const formData = new URLSearchParams();
    formData.append('name', productData.name);
    if (productData.description) formData.append('description', productData.description);
    formData.append('metadata[plan_slug]', productData.metadata?.plan_slug || '');

    try {
      const response = await this.makeRequest('/products', {
        method: 'POST',
        body: formData,
      });

      console.log('✅ Produto Stripe criado:', response.id);
      return response;
    } catch (error) {
      console.error('❌ Erro ao criar produto Stripe:', error);
      throw error;
    }
  }

  async createPrice(priceData: {
    product: string;
    unit_amount: number;
    currency: string;
    recurring?: { interval: 'month' | 'year'; interval_count?: number };
  }) {
    console.log('💰 Criando preço Stripe:', priceData);
    
    const formData = new URLSearchParams();
    formData.append('product', priceData.product);
    formData.append('unit_amount', priceData.unit_amount.toString());
    formData.append('currency', priceData.currency);
    
    if (priceData.recurring) {
      formData.append('recurring[interval]', priceData.recurring.interval);
      if (priceData.recurring.interval_count) {
        formData.append('recurring[interval_count]', priceData.recurring.interval_count.toString());
      }
    }

    try {
      const response = await this.makeRequest('/prices', {
        method: 'POST',
        body: formData,
      });

      console.log('✅ Preço Stripe criado:', response.id);
      return response;
    } catch (error) {
      console.error('❌ Erro ao criar preço Stripe:', error);
      throw error;
    }
  }

  async syncPlansWithStripe(localPlans: Plan[]) {
    console.log('🔄 Sincronizando planos com Stripe...');
    
    const stripeProducts = [];

    for (const plan of localPlans) {
      if (plan.slug === 'free') {
        console.log('⏭️ Pulando plano free (não precisa estar no Stripe)');
        continue;
      }

      try {
        // Criar produto
        const product = await this.createProduct({
          name: plan.name,
          description: plan.description,
          metadata: { plan_slug: plan.slug }
        });

        // Criar preços (mensal e anual)
        const monthlyPrice = await this.createPrice({
          product: product.id,
          unit_amount: Math.round(plan.priceMonthly * 100), // converter para centavos
          currency: 'brl',
          recurring: { interval: 'month' }
        });

        let yearlyPrice = null;
        if (plan.priceYearly > 0) {
          yearlyPrice = await this.createPrice({
            product: product.id,
            unit_amount: Math.round(plan.priceYearly * 100),
            currency: 'brl',
            recurring: { interval: 'year' }
          });
        }

        stripeProducts.push({
          plan: plan,
          product: product,
          monthlyPrice: monthlyPrice,
          yearlyPrice: yearlyPrice
        });

        console.log(`✅ Plano ${plan.name} sincronizado com Stripe`);
      } catch (error) {
        console.error(`❌ Erro ao sincronizar plano ${plan.name}:`, error);
      }
    }

    return stripeProducts;
  }

  // =====================================================
  // CHECKOUT E ASSINATURAS
  // =====================================================

  async createCheckoutSession(params: {
    customer_id?: string;
    price_id: string;
    success_url: string;
    cancel_url: string;
    metadata?: Record<string, string>;
  }) {
    console.log('💳 Criando sessão de checkout Stripe:', params);
    
    const formData = new URLSearchParams();
    formData.append('mode', 'subscription');
    formData.append('line_items[0][price]', params.price_id);
    formData.append('line_items[0][quantity]', '1');
    formData.append('success_url', params.success_url);
    formData.append('cancel_url', params.cancel_url);
    
    if (params.customer_id) {
      formData.append('customer', params.customer_id);
    }

    if (params.metadata) {
      Object.entries(params.metadata).forEach(([key, value]) => {
        formData.append(`metadata[${key}]`, value);
      });
    }

    try {
      const response = await this.makeRequest('/checkout/sessions', {
        method: 'POST',
        body: formData,
      });

      console.log('✅ Sessão de checkout criada:', response.id);
      return response;
    } catch (error) {
      console.error('❌ Erro ao criar sessão de checkout:', error);
      throw error;
    }
  }

  async createSubscription(subscriptionData: StripeSubscriptionRequest) {
    console.log('💳 Criando assinatura Stripe:', subscriptionData);
    
    const formData = new URLSearchParams();
    formData.append('customer', subscriptionData.customer_id);
    formData.append('items[0][price]', subscriptionData.price_id);
    
    if (subscriptionData.metadata) {
      Object.entries(subscriptionData.metadata).forEach(([key, value]) => {
        formData.append(`metadata[${key}]`, value);
      });
    }

    try {
      const response = await this.makeRequest('/subscriptions', {
        method: 'POST',
        body: formData,
      });

      console.log('✅ Assinatura Stripe criada:', response.id);
      return response;
    } catch (error) {
      console.error('❌ Erro ao criar assinatura Stripe:', error);
      throw error;
    }
  }

  async cancelSubscription(subscriptionId: string) {
    console.log('❌ Cancelando assinatura Stripe:', subscriptionId);
    
    try {
      const response = await this.makeRequest(`/subscriptions/${subscriptionId}`, {
        method: 'DELETE',
      });

      console.log('✅ Assinatura Stripe cancelada:', subscriptionId);
      return response;
    } catch (error) {
      console.error('❌ Erro ao cancelar assinatura Stripe:', error);
      throw error;
    }
  }

  async getSubscription(subscriptionId: string) {
    console.log('🔍 Buscando assinatura Stripe:', subscriptionId);
    
    try {
      const response = await this.makeRequest(`/subscriptions/${subscriptionId}`);
      return response;
    } catch (error) {
      console.error('❌ Erro ao buscar assinatura Stripe:', error);
      throw error;
    }
  }

  // =====================================================
  // WEBHOOK HANDLERS
  // =====================================================

  async handleWebhook(webhookData: any) {
    console.log('🔔 Webhook Stripe recebido:', webhookData.type);
    
    try {
      switch (webhookData.type) {
        case 'customer.subscription.created':
          return await this.handleSubscriptionCreated(webhookData.data);
          
        case 'customer.subscription.deleted':
          return await this.handleSubscriptionCanceled(webhookData.data);
          
        case 'invoice.payment_succeeded':
          return await this.handlePaymentSucceeded(webhookData.data);
          
        case 'invoice.payment_failed':
          return await this.handlePaymentFailed(webhookData.data);
          
        case 'checkout.session.completed':
          return await this.handleCheckoutCompleted(webhookData.data);
          
        default:
          console.log('⚠️ Tipo de webhook não tratado:', webhookData.type);
          return { processed: false, reason: 'webhook_type_not_handled' };
      }
    } catch (error) {
      console.error('❌ Erro ao processar webhook:', error);
      throw error;
    }
  }

  private async handleSubscriptionCreated(data: any) {
    console.log('✅ Processando criação de assinatura:', data.object?.id);
    return { processed: true, action: 'subscription_activated' };
  }

  private async handleSubscriptionCanceled(data: any) {
    console.log('❌ Processando cancelamento de assinatura:', data.object?.id);
    return { processed: true, action: 'subscription_deactivated' };
  }

  private async handlePaymentSucceeded(data: any) {
    console.log('💰 Processando pagamento confirmado:', data.object?.id);
    return { processed: true, action: 'payment_confirmed' };
  }

  private async handlePaymentFailed(data: any) {
    console.log('💸 Processando pagamento rejeitado:', data.object?.id);
    return { processed: true, action: 'payment_rejected' };
  }

  private async handleCheckoutCompleted(data: any) {
    console.log('🎉 Processando checkout completado:', data.object?.id);
    return { processed: true, action: 'checkout_completed' };
  }

  // =====================================================
  // UTILIDADES
  // =====================================================

  formatMoneyToCents(value: number): number {
    return Math.round(value * 100);
  }

  formatCentsToMoney(cents: number): number {
    return cents / 100;
  }

  isConfigured(): boolean {
    return !!(this.apiKey && this.publishableKey);
  }

  getStatus(): string {
    return this.isConfigured() ? 'configured' : 'simulated';
  }

  getPublishableKey(): string {
    return this.publishableKey;
  }
}

// =====================================================
// INSTÂNCIA SINGLETON
// =====================================================
export const stripeService = new StripeService();

// =====================================================
// FUNÇÕES DE CONVENIÊNCIA PARA INTEGRAÇÃO
// =====================================================

/**
 * Cria um cliente Stripe baseado nos dados do tenant
 */
export async function createStripeCustomerFromTenant(tenant: any) {
  const customerData: StripeCustomer = {
    name: tenant.name,
    email: tenant.contact?.email || `contato@${tenant.domain}`,
    phone: tenant.contact?.phone,
    address: tenant.contact?.address ? {
      line1: tenant.contact.address,
      city: 'São Paulo',
      state: 'SP',
      postal_code: '00000-000',
      country: 'BR'
    } : undefined
  };

  return await stripeService.createCustomer(customerData);
}

/**
 * Cria uma sessão de checkout para Premium
 */
export async function createPremiumCheckout(tenantId: string, priceId: string, stripeCustomerId?: string) {
  const baseUrl = window.location.origin;
  
  return await stripeService.createCheckoutSession({
    customer_id: stripeCustomerId,
    price_id: priceId,
    success_url: `${baseUrl}/checkout/success?session_id={CHECKOUT_SESSION_ID}`,
    cancel_url: `${baseUrl}/checkout/cancel`,
    metadata: {
      tenant_id: tenantId,
      plan_slug: 'premium'
    }
  });
}

/**
 * Verifica status de configuração do Stripe
 */
export function getStripeStatus() {
  return {
    isConfigured: stripeService.isConfigured(),
    status: stripeService.getStatus(),
    publishableKey: stripeService.getPublishableKey(),
    message: stripeService.isConfigured() 
      ? '✅ Stripe configurado e funcionando'
      : '🎭 Stripe em modo simulado - configure as chaves API'
  };
}
